import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../../config";
import { showToast } from "../../components/toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUp, faTimes } from "@fortawesome/free-solid-svg-icons";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const EditServicePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("authToken");

  const [pageData, setPageData] = useState({
    title: "",
    heading: "",
    seo_title: "",
    seo_keywords: "",
    seo_desc: "",
    description: "",
    slug: "",
    text_heading: "",
    text_description: "",
    description2: "",
    youtube_url: "",
    description3: "",
    data_section: [],
    faqs: [],
  });

  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [operation, setOperation] = useState("Add");
  const managePage = `${operation} Page`;

  // useEffect(() => {
  //   if (id) {
  //     setOperation("Edit");
  //     fetchPageData();
  //   }
  // }, [id]);

  useEffect(() => {
    if (id) {
      setOperation("Edit");
      fetchPageData();
    } else {
      setOperation("Add");
      // Reset form for Add mode
      setPageData({
        title: "",
        heading: "",
        seo_title: "",
        seo_keywords: "",
        seo_desc: "",
        description: "",
        slug: "",
        text_heading: "",
        text_description: "",
        description2: "",
        youtube_url: "",
        description3: "",
        data_section: [],
        faqs: [],
      });
    }
  }, [id]);


  const fetchPageData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}service-pages/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data) {
        const { page, faqs } = response.data;
        setPageData({
          title: page.title || "",
          heading: page.heading || "",
          seo_title: page.seo_title || "",
          seo_keywords: page.seo_keywords || "",
          seo_desc: page.seo_desc || "",
          description: page.description || "",
          slug: page.slug || "",
          text_heading: page.text_heading || "",
          text_description: page.text_description || "",
          description2: page.description2 || "",
          youtube_url: page.youtube_url || "",
          description3: page.description3 || "",
          data_section: page.data_section ? JSON.parse(page.data_section) : [],
          faqs: faqs || [],
        });
      }
    } catch (error) {
      console.error("Error fetching page data:", error);
      showToast.error("Failed to load page data");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPageData((prev) => ({ ...prev, [name]: value }));
  };

  const handleRichTextChange = (value, field) => {
    setPageData((prev) => ({ ...prev, [field]: value }));
  };

  const handleDataSectionChange = (index, field, value) => {
    const updatedData = [...pageData.data_section];
    updatedData[index] = { ...updatedData[index], [field]: value };
    setPageData((prev) => ({ ...prev, data_section: updatedData }));
  };

  const handleFaqChange = (index, field, value) => {
    setPageData((prev) => {
      const updatedFaqs = [...prev.faqs];
      updatedFaqs[index] = { ...updatedFaqs[index], [field]: value };
      return { ...prev, faqs: updatedFaqs };
    });
  };

  const addDataSection = () => {
    setPageData((prev) => ({
      ...prev,
      data_section: [
        ...prev.data_section,
        { imagefile: "", heading: "", desc: "", imageFile: null },
      ],
    }));
  };

  const removeDataSection = (index) => {
    const updatedData = [...pageData.data_section];
    updatedData.splice(index, 1);
    setPageData((prev) => ({ ...prev, data_section: updatedData }));
  };

  const addFaq = () => {
    setPageData((prev) => ({
      ...prev,
      faqs: [...prev.faqs, { title: "", description: "" }],
    }));
  };

  const removeFaq = (index) => {
    const updatedFaqs = [...pageData.faqs];
    updatedFaqs.splice(index, 1);
    setPageData((prev) => ({ ...prev, faqs: updatedFaqs }));
  };

  const handleFileUpload = (e, index) => {
    const file = e.target.files[0];
    if (!file) return;

    const updatedData = [...pageData.data_section];
    updatedData[index] = {
      ...updatedData[index],
      imageFile: file,
      imagefile: URL.createObjectURL(file), // For preview
    };
    setPageData((prev) => ({ ...prev, data_section: updatedData }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!pageData.title) {
      showToast.error("Title is required");
      return;
    }

    try {
      setLoading(true);

      // Create FormData for multipart/form-data
      const formData = new FormData();

      // Append all page data
      formData.append("title", pageData.title);
      formData.append("heading", pageData.heading);
      formData.append("seo_title", pageData.seo_title);
      formData.append("seo_keywords", pageData.seo_keywords);
      formData.append("seo_desc", pageData.seo_desc);
      formData.append("description", pageData.description);
      formData.append("slug", pageData.slug);
      formData.append("text_heading", pageData.text_heading);
      formData.append("text_description", pageData.text_description);
      formData.append("description2", pageData.description2);
      formData.append("youtube_url", pageData.youtube_url);
      formData.append("description3", pageData.description3);

      // Append data section
      const dataSectionWithFiles = pageData.data_section.map((section) => {
        if (section.imageFile) {
          return {
            ...section,
            imagefile: section.imageFile.name, // We'll handle the actual file separately
          };
        }
        return section;
      });
      formData.append("data_section", JSON.stringify(dataSectionWithFiles));

      // Append FAQs
      formData.append("faqs", JSON.stringify(pageData.faqs));

      // If editing, append the id
      if (id) {
        formData.append("id", id);
      }

      // Append all image files
      pageData.data_section.forEach((section, index) => {
        if (section.imageFile) {
          formData.append(`images[${index}]`, section.imageFile);
        }
      });

      const response = await axios.post(
        `${API_URL}service-pages/save`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.status === 201) {
        showToast.success(response.data.message || "Page saved successfully");
        navigate("/Seo/ServicePages");
      } else {
        showToast.error(response.data?.message || "Failed to save page");
      }
    } catch (error) {
      console.error("Save error:", error);
      showToast.error(error.response?.data?.message || "Failed to save page");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      id="page-container"
      className="page-header-fixed page-sidebar-fixed fade in"
    >
      <div id="content" className="">
        {loading && (
          <div className="overlay">
            <div className="spinner"></div>
          </div>
        )}

        <div className="panel panel-default with-rounded-corner with-shadow">
          <form
            onSubmit={handleSubmit}
            className="stdform"
            encType="multipart/form-data"
          >
            <input type="hidden" name="id" value={id || ""} />
            <div className="panel-heading">
              <span className="panel-title">{managePage}</span>
            </div>
            <div className="panel-body">
              <div className="row">
                <div className="form-group col-md-4">
                  <label className="control-label">Title</label>
                  <input
                    type="text"
                    className="form-control"
                    name="title"
                    placeholder="Enter Title"
                    value={pageData.title}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group col-md-4">
                  <label className="control-label">Heading</label>
                  <input
                    type="text"
                    className="form-control"
                    name="heading"
                    placeholder="Enter Heading"
                    value={pageData.heading}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group col-md-4">
                  <label className="control-label">URL</label>
                  <input
                    type="text"
                    className="form-control"
                    name="slug"
                    placeholder="Enter URL"
                    value={pageData.slug}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group col-md-4">
                  <label className="control-label">Seo Title</label>
                  <input
                    type="text"
                    className="form-control"
                    name="seo_title"
                    placeholder="Enter Seo Title"
                    value={pageData.seo_title}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group col-md-4">
                  <label className="control-label">Seo Keywords</label>
                  <input
                    type="text"
                    className="form-control"
                    name="seo_keywords"
                    placeholder="Enter Seo Keyword"
                    value={pageData.seo_keywords}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group col-md-4">
                  <label className="control-label">Seo Description</label>
                  <input
                    type="text"
                    className="form-control"
                    name="seo_desc"
                    placeholder="Enter Seo Description"
                    value={pageData.seo_desc}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group col-md-12">
                  <label className="control-label">Description 1</label>
                  <ReactQuill
                    theme="snow"
                    value={pageData.description}
                    onChange={(value) =>
                      handleRichTextChange(value, "description")
                    }
                  />
                </div>

                <div className="form-group col-md-6">
                  <label className="control-label">Text Heading</label>
                  <input
                    type="text"
                    className="form-control"
                    name="text_heading"
                    placeholder="Enter Text Heading"
                    value={pageData.text_heading}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group col-md-6">
                  <label className="control-label">Text Description</label>
                  <textarea
                    className="form-control"
                    name="text_description"
                    placeholder="Enter Text Description"
                    value={pageData.text_description}
                    onChange={handleChange}
                    rows="3"
                  />
                </div>
              </div>

              {/* Data Section */}
              <div className="faq_div mt-5">
                <div className="panel-heading p-0">
                  <span className="panel-title">Data Section</span>
                </div>
                <div className="row clonelist">
                  <table className="table m-b-0 col-12">
                    <thead>
                      <tr>
                        <th style={{ textAlign: "center", width: "30%" }}>
                          <div className="th-inner">Image Upload</div>
                        </th>
                        <th style={{ textAlign: "center", width: "30%" }}>
                          <div className="th-inner">Heading</div>
                        </th>
                        <th style={{ textAlign: "center", width: "30%" }}>
                          <div className="th-inner">Description</div>
                        </th>
                        <th style={{ textAlign: "center", width: "10%" }}>
                          <div className="th-inner">Delete</div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {pageData.data_section.map((item, index) => (
                        <tr key={`data-${index}`}>
                          <td>
                            <div className="form-group">
                              <input
                                type="file"
                                className="form-control"
                                onChange={(e) => handleFileUpload(e, index)}
                                accept="image/*"
                              />
                            </div>
                            {item.imagefile && (
                              <div className="col-12 old-img-block mt-2">
                                <img
                                  src={item.imagefile}
                                  width="30%"
                                  alt="Preview"
                                />
                              </div>
                            )}
                          </td>
                          <td>
                            <div className="form-group">
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Heading"
                                value={item.heading}
                                onChange={(e) =>
                                  handleDataSectionChange(
                                    index,
                                    "heading",
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                          </td>
                          <td>
                            <div className="form-group">
                              <textarea
                                className="form-control"
                                placeholder="Description"
                                value={item.desc}
                                onChange={(e) =>
                                  handleDataSectionChange(
                                    index,
                                    "desc",
                                    e.target.value
                                  )
                                }
                                rows="3"
                              />
                            </div>
                          </td>
                          <td
                            style={{
                              textAlign: "center",
                              verticalAlign: "middle",
                            }}
                          >
                            <button
                              type="button"
                              className="btn btn-danger btn-gradient delete_row btn-alt pt5 pb5 mn"
                              title="Delete Record"
                              onClick={() => removeDataSection(index)}
                            >
                              <FontAwesomeIcon icon={faTimes} /> Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                      <tr>
                        <td colSpan="4">
                          <button
                            type="button"
                            className="adddataPick btn btn-success"
                            onClick={addDataSection}
                          >
                            Add Data
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="row mt-3">
                <div className="form-group col-md-12">
                  <label className="control-label">Description 2</label>
                  <ReactQuill
                    theme="snow"
                    value={pageData.description2}
                    onChange={(value) =>
                      handleRichTextChange(value, "description2")
                    }
                  />
                </div>

                <div className="form-group col-md-12">
                  <label className="control-label">Youtube Url</label>
                  <input
                    type="text"
                    className="form-control"
                    name="youtube_url"
                    placeholder="Enter Youtube Url"
                    value={pageData.youtube_url}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group col-md-12">
                  <label className="control-label">Description 3</label>
                  <ReactQuill
                    theme="snow"
                    value={pageData.description3}
                    onChange={(value) =>
                      handleRichTextChange(value, "description3")
                    }
                  />
                </div>
              </div>

              {/* FAQ Section */}
              <div className="faq_div mt-5">
                <div className="panel-heading p-0">
                  <span className="panel-title">
                    Frequently Asked Questions
                  </span>
                </div>
                <div className="row clonelist">
                  <table className="table m-b-0 col-12">
                    <thead>
                      <tr>
                        <th style={{ textAlign: "center", width: "30%" }}>
                          <div className="th-inner">Title</div>
                        </th>
                        <th style={{ textAlign: "center", width: "30%" }}>
                          <div className="th-inner">Description</div>
                        </th>
                        <th style={{ textAlign: "center", width: "10%" }}>
                          <div className="th-inner">Delete</div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {pageData.faqs.map((faq, index) => (
                        <tr key={`faq-${index}`}>
                          <td>
                            <div className="form-group">
                              <input
                                type="text"
                                className="form-control"
                                placeholder="FAQ Question"
                                value={faq.title}
                                onChange={(e) =>
                                  handleFaqChange(
                                    index,
                                    "title",
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                          </td>
                          <td>
                            <div className="form-group">
                              <ReactQuill
                                key={`faq-desc-${index}`}
                                theme="snow"
                                value={faq.description}
                                onChange={(value) =>
                                  handleFaqChange(index, "description", value)
                                }
                              />
                            </div>
                          </td>
                          <td
                            style={{
                              textAlign: "center",
                              verticalAlign: "middle",
                            }}
                          >
                            <button
                              type="button"
                              className="btn btn-danger btn-gradient delete_row btn-alt pt5 pb5 mn"
                              title="Delete Record"
                              onClick={() => removeFaq(index)}
                            >
                              <FontAwesomeIcon icon={faTimes} /> Delete
                            </button>
                          </td>
                        </tr>
                      ))}

                      <tr>
                        <td colSpan="3">
                          <button
                            type="button"
                            className="addPick btn btn-success"
                            onClick={addFaq}
                          >
                            Add Data
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="form-group mt-3 text-left">
                <button
                  type="submit"
                  className="btn btn-primary btn-sm"
                  disabled={loading}
                >
                  {loading ? "Saving..." : "Save"}
                </button>
                <button
                  type="button"
                  className="btn btn-default btn-sm ml-2 ms-2"
                  onClick={() => navigate("/Seo/ServicePages")}
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </div>

        <a href="#" data-click="scroll-top" className="btn-scroll-top fade">
          <FontAwesomeIcon icon={faArrowUp} />
        </a>
      </div>
    </div>
  );
};

export default EditServicePage;
